// Casting mechanic: bobber/line rendering, bite detection, catch resolution.
// Extracted from legacy/tsuri.html — logic and constants are unchanged.
//
// Storage (which basket/boat/cart a catch goes into), stats, and toast
// messages are NOT decided here — those need inventory (economy.ts, step 7)
// and boat/cart (building.ts, step 8), which don't exist as modules yet.
// updateCast reports what happened via a CastEvent instead, for the caller
// (main.ts, step 10) to act on.

import * as THREE from 'three';
import { waterInfoAt } from '@world/water';
import { PROP_SCALE } from '@world/terrain';
import { toonMat } from '@world/toon';
import { player, rodTipMarker } from '@entities/player';
import { FISH_TYPES, findFishWithin, removeFish, type FishInstance } from '@entities/fish';

export let CAST_RANGE = 3.6; // grows with rod upgrades
export function increaseCastRange(amount: number): void {
  CAST_RANGE += amount;
}

export const BITE_RADIUS = 0.5;

export type CastPhase = 'idle' | 'waiting' | 'bite';

export interface CastState {
  active: boolean;
  phase: CastPhase;
  timer: number;
  x: number;
  z: number;
  fish: FishInstance | null;
}

export const cast: CastState = { active: false, phase: 'idle', timer: 0, x: 0, z: 0, fish: null };

export const bobber = new THREE.Mesh(new THREE.SphereGeometry(0.05 * PROP_SCALE, 6, 5), toonMat(0xc4483c));
bobber.visible = false;

const lineGeo = new THREE.BufferGeometry();
const linePositions = new Float32Array(10 * 3);
lineGeo.setAttribute('position', new THREE.BufferAttribute(linePositions, 3));
export const fishingLine = new THREE.Line(lineGeo, new THREE.LineBasicMaterial({ color: 0xeeeeee, transparent: true, opacity: 0.8 }));
fishingLine.visible = false;
fishingLine.frustumCulled = false;

export function startCast(x: number, z: number, surfaceY: number): void {
  cast.active = true;
  cast.phase = 'waiting';
  cast.timer = 0;
  cast.x = x; cast.z = z;
  bobber.position.set(x, surfaceY + 0.02 * PROP_SCALE, z);
  bobber.visible = true;
  fishingLine.visible = true;
  // Face the cast target
  player.heading = Math.atan2(z - player.z, x - player.x);
}

export function endCast(): void {
  cast.active = false;
  cast.phase = 'idle';
  cast.fish = null;
  bobber.visible = false;
  fishingLine.visible = false;
}

// Gear (rod level) and grogginess (rest) both matter; rain helps bites.
export function computeBiteRadius(rodLevel: number, restBiteMod: number, isRaining: boolean): number {
  let r = BITE_RADIUS + (rodLevel - 1) * 0.08 + restBiteMod;
  if (isRaining) r += 0.25;
  return Math.max(0.3, r);
}

export interface FishingConditions {
  isStormy: boolean;
  inBoat: boolean;
  isNight: boolean;
  isFullMoon: boolean;
  isRaining: boolean;
  rodLevel: number;
  restBiteMod: number;
}

export function computeFishingBlockReason(cond: Pick<FishingConditions, 'isStormy' | 'inBoat' | 'isNight' | 'isFullMoon'>): string | null {
  if (cond.isStormy && cond.inBoat) return '⛈️ Too rough to fish from the boat — try the shore!';
  if (cond.isNight && !cond.isFullMoon) return '🌙 Too dark to fish — wait for dawn, or a full moon.';
  return null;
}

export type CastEvent =
  | { type: 'blocked'; reason: string }
  | { type: 'no-bite' }
  | { type: 'caught'; fish: FishInstance; typeName: string; x: number; z: number };

export function updateCast(dt: number, now: number, cond: FishingConditions): CastEvent | null {
  if (!cast.active) return null;
  const blockReason = computeFishingBlockReason(cond);
  if (blockReason) {
    endCast();
    return { type: 'blocked', reason: blockReason };
  }
  cast.timer += dt;
  const w = waterInfoAt(cast.x, cast.z);
  const surfY = w ? w.surfaceY : 0;

  let event: CastEvent | null = null;

  if (cast.phase === 'waiting') {
    bobber.position.y = surfY + 0.02 + Math.sin(now * 0.004) * 0.015;
    // A fish close to the bobber takes the bait
    const biteRadius = computeBiteRadius(cond.rodLevel, cond.restBiteMod, cond.isRaining);
    const near = findFishWithin(cast.x, cast.z, biteRadius);
    if (near) {
      cast.phase = 'bite';
      cast.timer = 0;
      cast.fish = near;
    } else if (cast.timer > 6) {
      event = { type: 'no-bite' };
      endCast();
    }
  } else if (cast.phase === 'bite') {
    bobber.position.y = surfY - 0.05 + Math.sin(now * 0.03) * 0.02; // bobber dips
    if (cast.timer > 0.5) {
      const f = cast.fish;
      if (f) {
        const t = FISH_TYPES[f.typeIndex];
        removeFish(f);
        event = { type: 'caught', fish: f, typeName: t.name, x: cast.x, z: cast.z };
      }
      endCast();
    }
  }

  // Update the sagging line from rod tip to bobber
  const tip = new THREE.Vector3();
  rodTipMarker.getWorldPosition(tip);
  const end = bobber.position;
  for (let i = 0; i < 10; i++) {
    const tt = i / 9;
    const px = tip.x + (end.x - tip.x) * tt;
    const pz = tip.z + (end.z - tip.z) * tt;
    const sag = Math.sin(tt * Math.PI) * 0.18;
    const py = tip.y + (end.y - tip.y) * tt - sag;
    linePositions[i * 3] = px;
    linePositions[i * 3 + 1] = py;
    linePositions[i * 3 + 2] = pz;
  }
  lineGeo.attributes.position.needsUpdate = true;

  return event;
}
